/**
 * 대용량 첨부 업로드 큐.
 *
 * 일반 첨부와 흐름이 정반대라 별도 훅으로 뺐다. 일반 첨부는 File 객체로 메모리에만 있다가 발송 시
 * multipart 로 한 번에 올라가지만, 대용량은 <b>작성 중에</b> 브라우저에서 S3 로 직접 올라간다.
 *
 * 왜 그래야 하나
 *   2GB 를 발송 시점에 올리기 시작하면 사용자가 [보내기] 를 누르고 수십 분을 기다려야 하고,
 *   중간에 끊기면 발송 자체가 실패한다. 미리 올려 두면 발송은 즉시 끝나고, 업로드 도중 탭이
 *   닫혀도 초안으로 돌아와 이어갈 수 있다.
 *
 * 동시 몇 개까지 올리나 (MAX_CONCURRENT)
 *   무제한도 1개도 아니다. 큰 파일 하나만 있으면 한 연결로도 회선을 다 쓰므로 병렬이 이득이 없지만,
 *   파일마다 presign·complete 왕복이 붙어서 중간 크기 여러 개를 순차로 돌리면 그 왕복이 직렬로 쌓인다.
 *   무엇보다 20MB 짜리가 1.1GB 뒤에서 몇 분을 기다리는 게 실사용에서 제일 답답하다.
 *   몇 개만 겹치면 왕복이 서로 가려지고 작은 파일이 먼저 끝난다.
 *
 * 상태 전이
 *   queued → presigning → uploading → completing → done
 *                                   ↘ error (재시도 가능: represign 으로 같은 키에 다시 올린다)
 */
import { useCallback, useRef, useState } from "react";
import { mailApi } from "../../api/mailApi";

let seq = 0;
const nextKey = () => `__la__${seq++}`;

export const LARGE_STATUS = {
    QUEUED:     "queued",
    PRESIGNING: "presigning",
    UPLOADING:  "uploading",
    COMPLETING: "completing",
    DONE:       "done",
    ERROR:      "error",
};

/**
 * 동시 업로드 개수.
 *
 * 1(순차)이면 20MB 짜리가 1.1GB 뒤에서 몇 분을 기다린다. 크게 올리면 좁은 사무실 회선을
 * 서로 나눠 먹어 전부 느려지고 진행률도 읽기 어려워진다. 3은 그 사이다 —
 * 브라우저가 호스트당 6개 연결까지 허용하므로 여유도 있다.
 */
const MAX_CONCURRENT = 3;

/** 업로드가 끝나지 않아 발송을 막아야 하는 상태들. */
const BUSY = [LARGE_STATUS.QUEUED, LARGE_STATUS.PRESIGNING, LARGE_STATUS.UPLOADING, LARGE_STATUS.COMPLETING];

/**
 * @param ensureDraft () => Promise<number>  초안 id 를 보장해 돌려주는 콜백.
 *   presign 에 mailId 가 필요한데 작성창은 발송 전까지 초안을 만들지 않으므로,
 *   첫 대용량 파일을 고를 때 저장을 선행시킨다. (그 덕에 긴 업로드 중 탭이 닫혀도 복구된다.)
 * @param notify (msg, level) 사용자 알림
 */
export function useLargeAttachments({ ensureDraft, notify }) {
    const [items, setItems] = useState([]);
    /*
      서버 한도·문구 설정. 프론트가 상수로 들고 있으면 mail.yml 을 바꿨을 때 화면만 거짓말을 한다
      ("30일 보관"이라고 써놓고 실제로는 14일 뒤 삭제되는 식).
    */
    const [quota, setQuota] = useState(null);

    // 업로드 중인 XHR 핸들 — 제거·취소 시 중단하려면 필요하다.
    const abortersRef = useRef(new Map());   // key -> abort()
    /**
     * 지금 올라가는 중인 개수. MAX_CONCURRENT 를 넘지 않게 하는 카운터다.
     *
     * 왜 무제한이 아닌가: 회선 대역폭은 고정이라 동시 수를 늘려도 총 시간은 줄지 않고,
     * 너무 많으면 서로 밀어내 큰 파일 하나가 끝없이 느려진다. 반대로 1개(순차)면 20MB 짜리가
     * 1.1GB 뒤에서 통째로 기다린다 — 파일마다 presign·complete 왕복도 직렬로 쌓인다.
     * 몇 개만 겹치면 그 왕복이 서로 가려지고, 작은 파일은 먼저 끝난다.
     */
    const runningRef = useRef(0);
    /**
     * 큐 루프가 읽는 '최신 목록'. 아래 commit() 이 유일한 writer 다.
     *
     * ★ 렌더마다 itemsRef.current = items 로 덮으면 안 된다. 다른 state 변경으로 렌더가 끼어들면
     *   아직 반영 전인 items 로 되돌아가, 방금 큐에 넣은 항목이 통째로 사라진다.
     */
    const itemsRef = useRef([]);

    // 콜백도 ref 로 잡는다. 호출자가 매 렌더 새 함수를 넘기는데(작성창 state 에 의존하므로),
    // 그대로 deps 에 넣으면 큐 소비 루프가 재생성돼 동시 실행 수 관리가 깨진다.
    const cbRef = useRef({ ensureDraft, notify });
    cbRef.current = { ensureDraft, notify };
    // 업로드 완료 시 잔여 용량을 다시 읽기 위한 연결(정의는 아래).
    const refreshQuotaRef = useRef(null);
    /** load 가 deps 없이 쓰기 위한 연결(정의는 아래 refreshQuota). */
    const quotaFnRef = useRef(null);

    /**
     * 목록 갱신의 유일한 통로 — ref 를 '먼저' 맞추고 state 를 따라가게 한다.
     *
     * ★ setItems(prev => ...) 의 updater 안에서 ref 를 갱신하면 안 된다. React 18 은 그 함수를
     *   렌더 시점에 실행하므로, 이벤트 핸들러에서 바로 뒤에 pump() 를 부르면 아직 옛 배열을 보고
     *   "올릴 게 없다" 며 끝난다 — 큐가 QUEUED 인 채로 영원히 멈춘다.
     *   파일을 여러 개 붙이면 setAttachments 와 배치로 묶여 이 어긋남이 확실히 드러난다(실측).
     */
    const commit = useCallback((next) => {
        itemsRef.current = next;
        setItems(next);
    }, []);

    const patch = useCallback((key, changes) => {
        commit(itemsRef.current.map(it => (it.key === key ? { ...it, ...changes } : it)));
    }, [commit]);

    // ── 큐 소비 (동시 MAX_CONCURRENT 개) ────────────────────────────────────

    // uploadOne 은 아래에서 정의되므로 ref 로 연결한다(선언 순서 회피 + 항상 최신 참조).
    const uploadOneRef = useRef(null);

    const pump = useCallback(() => {
        while (runningRef.current < MAX_CONCURRENT) {
            const target = itemsRef.current.find(it => it.status === LARGE_STATUS.QUEUED);
            if (!target) break;

            // ★ 집어가는 즉시 상태를 바꾼다. commit 이 ref 를 동기로 맞추므로 아래 find 가
            //   같은 항목을 다시 고르지 않는다. 이게 없으면 한 파일을 여러 워커가 동시에 올린다.
            patch(target.key, { status: LARGE_STATUS.PRESIGNING, error: null });

            runningRef.current += 1;
            Promise.resolve(uploadOneRef.current?.(target.key))
                .finally(() => {
                    runningRef.current -= 1;
                    pump();   // 슬롯이 비면 다음 대기분을 집어간다
                });
        }
    }, [patch]);

    const uploadOne = useCallback(async (key) => {
        const it = itemsRef.current.find(x => x.key === key);
        if (!it) return;

        // ★ 파일 참조가 없으면 절대 올리지 않는다.
        //   초안을 다시 열면 서버 목록만 복원되고 File 객체는 없다(브라우저 메모리라 세션을 못 넘는다).
        //   그 상태로 진행하면 xhr.send(null) 이 나가 S3 에 0바이트 객체가 생기고,
        //   complete 의 headObject 는 0을 정상 크기로 받아들여 '빈 첨부' 가 발송된다.
        if (!it.file) {
            patch(key, {
                status: LARGE_STATUS.ERROR,
                error: "이 첨부는 다시 올릴 수 없습니다. 삭제 후 파일을 다시 선택해 주세요.",
            });
            return;
        }

        try {
            // 1) 초안 확보 → presign  (PRESIGNING 은 pump 가 집어갈 때 이미 세팅했다)
            const mailId = await cbRef.current.ensureDraft();
            if (!mailId) throw new Error("임시저장에 실패해 업로드를 시작할 수 없습니다.");

            let serverId = it.serverId;
            let uploadUrl;
            if (serverId) {
                // 재시도 — 행은 이미 있으므로 같은 S3 키로 URL 만 다시 받는다.
                uploadUrl = (await mailApi.largeRepresign(serverId))?.uploadUrl;
            } else {
                const res = await mailApi.largePresign({
                    mailId,
                    filename:  it.filename,
                    sizeBytes: it.sizeBytes,
                    mimeType:  it.mimeType,
                });
                serverId  = res?.id;
                uploadUrl = res?.uploadUrl;
                patch(key, { serverId });
            }
            if (!uploadUrl) throw new Error("업로드 주소를 받지 못했습니다.");

            // 2) S3 로 직접 PUT
            const startedAt = Date.now();
            patch(key, { status: LARGE_STATUS.UPLOADING, progress: 0, etaSec: null });

            const { promise, abort } = mailApi.largeUploadToS3(uploadUrl, it.file, (loaded, total) => {
                const pct = total > 0 ? Math.round((loaded / total) * 100) : 0;
                const elapsed = (Date.now() - startedAt) / 1000;
                // 남은 시간 추정 — 진행률이 없으면 계산이 무의미하므로 3% 부터 표시한다.
                const etaSec = (loaded > 0 && elapsed > 1 && pct >= 3)
                    ? Math.max(1, Math.round((total - loaded) / (loaded / elapsed)))
                    : null;
                patch(key, { progress: pct, etaSec });
            });
            abortersRef.current.set(key, abort);
            try {
                await promise;
            } finally {
                abortersRef.current.delete(key);
            }

            // 3) complete — 서버가 headObject 로 실제 크기를 확인해 신고값을 덮어쓴다.
            patch(key, { status: LARGE_STATUS.COMPLETING, progress: 100, etaSec: null });
            const dto = await mailApi.largeComplete(serverId, it.mimeType);
            patch(key, {
                status: LARGE_STATUS.DONE,
                sizeBytes: dto?.sizeBytes ?? it.sizeBytes,   // 실측값으로 교정
                expiresAt: dto?.expiresAt ?? null,
                daysLeft:  dto?.daysLeft ?? null,
                file: null,   // 업로드가 끝났으니 File 참조를 놓아준다
            });
            refreshQuotaRef.current?.();
        } catch (e) {
            if (e?.name === "AbortError") return;   // 사용자가 지운 것 — 조용히 종료
            const msg = e?.response?.data?.message || e?.message || "업로드에 실패했습니다.";
            patch(key, { status: LARGE_STATUS.ERROR, error: msg, etaSec: null });
            cbRef.current.notify?.(`${it.filename}: ${msg}`, "error");
        }
    }, [patch]);
    uploadOneRef.current = uploadOne;

    // ── 외부 API ────────────────────────────────────────────────────────────

    /** 대용량으로 올릴 파일 추가. picked: [{file, path}] */
    const add = useCallback((picked) => {
        if (!picked?.length) return;
        const created = picked.map(p => ({
            key: nextKey(),
            serverId: null,
            filename: String(p.path || p.file.name || "attachment").replace(/[\\/]+/g, "_"),
            sizeBytes: p.file.size,
            mimeType: p.file.type || "application/octet-stream",
            file: p.file,
            status: LARGE_STATUS.QUEUED,
            retryable: true,
            progress: 0,
            etaSec: null,
            error: null,
            expiresAt: null,
            daysLeft: null,
        }));
        commit([...itemsRef.current, ...created]);
        pump();
    }, [commit, pump]);

    /** 목록에서 제거 — 업로드 중이면 중단하고, 서버 행이 있으면 지운다. */
    const remove = useCallback(async (key) => {
        const it = itemsRef.current.find(x => x.key === key);
        abortersRef.current.get(key)?.();
        abortersRef.current.delete(key);
        commit(itemsRef.current.filter(x => x.key !== key));
        if (it?.serverId) {
            // 실패해도 사용자 흐름을 막지 않는다 — 서버 스윕이 회수한다.
            try { await mailApi.largeDelete(it.serverId); } catch { /* noop */ }
        }
        pump();
    }, [commit, pump]);

    /** 실패한 항목 재시도 — 같은 S3 키에 다시 올린다(첨부를 다시 고를 필요 없음). */
    const retry = useCallback((key) => {
        patch(key, { status: LARGE_STATUS.QUEUED, error: null, progress: 0 });
        pump();   // commit 이 ref 를 이미 맞췄으므로 곧바로 집어간다
    }, [patch, pump]);

    /**
     * 초안 재편집 — 서버에 이미 올라가 있는 목록을 복원한다.
     * (일반 첨부는 임시저장에 실리지 않아 복원되지 않는다. 대용량만 살아남는 게 정상이다.)
     */
    const load = useCallback(async (mailId) => {
        // ref 로 부른다 — refreshQuota 를 deps 에 넣으면 load 가 매번 새 함수가 되고,
        // 이 훅을 useEffect deps 에 쓰는 호출부에서 재실행 루프가 생긴다.
        quotaFnRef.current?.(mailId);
        if (!mailId) return;
        try {
            const rows = await mailApi.largeList(mailId);
            const restored = (rows || []).map(r => ({
                key: nextKey(),
                serverId: r.id,
                filename: r.filename,
                sizeBytes: r.sizeBytes,
                mimeType: r.mimeType,
                file: null,
                // 서버가 PENDING 으로 준 건 업로드가 끝나지 않은 것 — 파일 참조가 없어 이어받을 수
                // 없으므로 error 로 두어 사용자가 지우고 다시 붙이게 한다.
                status: r.status === "UPLOADED" ? LARGE_STATUS.DONE : LARGE_STATUS.ERROR,
                // 파일 참조가 없어 이어받을 수 없다 — 재시도 버튼도 숨긴다(retryable:false).
                error: r.status === "UPLOADED" ? null : "업로드가 완료되지 않았습니다. 삭제 후 다시 첨부해 주세요.",
                retryable: false,
                progress: r.status === "UPLOADED" ? 100 : 0,
                etaSec: null,
                expiresAt: r.expiresAt ?? null,
                daysLeft:  r.daysLeft ?? null,
            }));
            commit(restored);
        } catch { /* 복원 실패는 치명적이지 않다 — 빈 목록으로 시작 */ }
    }, [commit]);

    /** 잔여 용량·표시 설정 갱신. mailId 가 없어도 사용자 총량과 설정값은 받아온다. */
    const refreshQuota = useCallback(async (mailId) => {
        try { setQuota(await mailApi.largeQuota(mailId ?? undefined)); }
        catch { /* 표시용이라 실패해도 흐름을 막지 않는다 */ }
    }, []);

    /** 작성창을 닫거나 발송을 마쳤을 때 — 진행 중 업로드를 끊고 목록을 비운다. */
    const reset = useCallback(() => {
        abortersRef.current.forEach(fn => fn());
        abortersRef.current.clear();
        commit([]);
    }, [commit]);

    refreshQuotaRef.current = () => refreshQuota(undefined);
    quotaFnRef.current = refreshQuota;

    const busyCount  = items.filter(it => BUSY.includes(it.status)).length;
    const errorCount = items.filter(it => it.status === LARGE_STATUS.ERROR).length;
    const totalBytes = items.reduce((s, it) => s + (it.sizeBytes || 0), 0);

    return {
        items, add, remove, retry, load, reset, quota, refreshQuota,
        /** 업로드가 끝나지 않은 게 있으면 발송을 막아야 한다(서버도 막지만 UI 가 1차 방어). */
        busyCount,
        errorCount,
        totalBytes,
        hasBlocking: busyCount > 0,
    };
}
